import { Button, Icon } from '../../../ui';
import type { ViewIssue } from './viewState';

/**
 * D18's BANNER: what this view asked for that the model no longer supports, and the two
 * things a person can do about it.
 *
 * The whole rule it renders is "a view NEVER errors, it degrades and says so". So the
 * list below it is already running, narrowed to what still works - and NOTHING HAS BEEN
 * WRITTEN. Saving is an offer, not a consequence; reloading puts the damaged view back
 * exactly as it is stored, which is what makes it safe to look at a broken view without
 * being the person who destroyed it.
 *
 * `role="status"` rather than `alert`: the page is usable, this is a notice about it,
 * and an assertive interruption for something the reader can act on at their leisure is
 * the kind of thing that teaches people to ignore banners.
 */
export interface ViewIssueBannerProps {
  readonly issues: readonly ViewIssue[];
  readonly onSave: () => void;
  readonly isSaving: boolean;
}

export function ViewIssueBanner({ issues, onSave, isSaving }: ViewIssueBannerProps) {
  if (issues.length === 0) return null;

  return (
    <div
      role="status"
      className="flex flex-wrap items-start gap-3 rounded-surface border border-warning bg-warning-subtle px-4 py-3"
    >
      <Icon name="warning" className="mt-0.5 shrink-0 text-warning" size="1rem" />
      <div className="min-w-0 flex-1 space-y-1">
        {issues.map((issue) => (
          <p key={`${issue.kind}-${issue.message}`} className="text-sm text-ink">
            {issue.message}
          </p>
        ))}
        <p className="text-sm text-ink-muted">
          Nothing has been changed yet. Save the view to keep it this way, or reload the page to put
          it back as it was.
        </p>
      </div>
      <Button variant="secondary" size="sm" loading={isSaving} onClick={onSave}>
        Save
      </Button>
    </div>
  );
}
